// Product card component for displaying individual products
import React, { useState } from 'react';
import { Product, SelectedOption } from '../types';
import { useCartStore } from '../store/cartStore';
import { ProductOptionsModal } from './ProductOptionsModal';
import ImageModal from './ImageModal';
import { calculateDisplayPrice } from '../shared/pricing';
import { TEMPORARY_CLOSURE_ENABLED } from '../shared/temporaryClosure';
import './ProductCard.css';

interface ProductCardProps {
    product: Product;
    layout?: 'grid' | 'horizontal';
}

const ProductCard: React.FC<ProductCardProps> = ({ product, layout = 'grid' }) => {
    const { addToCart, cart } = useCartStore();
    const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);

    const availableStock = product.availableStock ?? null;
    const hasLimitedStock = availableStock !== null;
    const isOutOfStock = hasLimitedStock && availableStock <= 0;
    const isSoldOut = isOutOfStock && !product.allowStockException && product.available;
    const isUnavailable = !product.available || (isOutOfStock && !product.allowStockException);
    const isOrderingPaused = TEMPORARY_CLOSURE_ENABLED;

    // Get discount percentage from cart
    const discountPercentage = cart.discount?.percentage;
    const priceDisplay = calculateDisplayPrice(product.price, discountPercentage);

    const handleAddToCart = async () => {
        if (isUnavailable || isOrderingPaused) {
            return;
        }

        // Always open options modal regardless of whether options exist
        setIsOptionsModalOpen(true);
    };

    const handleOptionsModalAddToCart = async (selectedOptions: SelectedOption[], quantity: number) => {
        await addToCart(product, quantity, selectedOptions);
        setIsOptionsModalOpen(false);
    };

    const handleCardClick = (e: React.MouseEvent) => {
        // Don't open modal if clicking on the Add to Cart button
        if ((e.target as HTMLElement).closest('.add-to-cart-btn')) {
            return;
        }
        setIsImageModalOpen(true);
    };

    return (
        <>
            <div
                className={`product-card product-card-${layout} ${isSoldOut ? 'sold-out' : isUnavailable ? 'unavailable' : ''} ${isOrderingPaused ? 'ordering-paused' : ''}`}
                onClick={handleCardClick}
                style={{ cursor: 'pointer' }}
            >
                {isSoldOut && (
                    <div className="sold-out-banner">
                        <span>SOLD OUT</span>
                    </div>
                )}
                <div className="product-image">
                    <img
                        src={product.image}
                        alt={product.name}
                    />
                    <div className="image-zoom-hint">
                        <span>🔍 Click to zoom</span>
                    </div>
                    {isUnavailable && !isSoldOut && (
                        <div className="unavailable-overlay">
                            <span>
                                {isOutOfStock ? 'Out of Stock' : 'Temporarily Unavailable'}
                            </span>
                        </div>
                    )}
                </div>                <div className="product-info">
                    <h3 className="product-name">{product.name}</h3>

                    <p className="product-description">{product.description}</p>

                    {/* Stock information */}
                    {hasLimitedStock && (
                        <div className="stock-info">
                            {isOutOfStock ? (
                                product.allowStockException ? (
                                    <span className="stock-available">Available</span>
                                ) : (
                                    <span className="stock-out">Out of Stock</span>
                                )
                            ) : (product.allowStockException && availableStock < 4) ? (
                                <span className="stock-available">Available</span>
                            ) : availableStock <= 5 ? (
                                <span className="stock-low">Only {availableStock} left</span>
                            ) : (
                                <span className="stock-available">In Stock {product.allowStockException ? '' : `(${availableStock})`}</span>
                            )}
                        </div>
                    )}

                    <div className="product-footer">
                        {/* Campaign Badge */}
                        {product.campaignText && (
                            <div className="campaign-badge">
                                {product.campaignText}
                            </div>
                        )}

                        <div className="product-footer-row">
                            <div className="product-price">
                                {/* Show strikethrough price if discount is active */}
                                {priceDisplay.hasDiscount && priceDisplay.originalPrice && (
                                    <span className="original-price">
                                        £{priceDisplay.originalPrice.toFixed(2)}
                                    </span>
                                )}
                                <div className="current-price">
                                    <span className="price-label">£</span>
                                    <span className="price-value">{priceDisplay.currentPrice.toFixed(2)}</span>
                                </div>
                            </div>

                            <button
                                className="add-to-cart-btn"
                                onClick={handleAddToCart}
                                disabled={isUnavailable || isOrderingPaused}
                            >
                                {isOrderingPaused ? 'Paused' :
                                    isSoldOut ? 'Sold Out' :
                                    isOutOfStock && !product.allowStockException ? 'Out of Stock' :
                                        !product.available ? 'Unavailable' :
                                            'See Options'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Product Options Modal */}
            <ProductOptionsModal
                product={product}
                isOpen={isOptionsModalOpen}
                onClose={() => setIsOptionsModalOpen(false)}
                onAddToCart={handleOptionsModalAddToCart}
            />

            {/* Image Modal */}
            <ImageModal
                isOpen={isImageModalOpen}
                imageUrl={product.image}
                imageName={product.name}
                product={product}
                onClose={() => setIsImageModalOpen(false)}
                onAddToCart={isUnavailable || isOrderingPaused ? undefined : handleAddToCart}
            />
        </>
    );
};

export default ProductCard;
